import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Lock, LockOpen, Trash2 } from "lucide-react";
import type { CustomerContactInput } from "@hisense/shared";
import {
  CONTACT_COLUMN_WIDTH, CONTACT_EDGE_COLUMNS_REM, CONTACT_FLEX_MIN_REM, CONTACT_MAIN_COLUMNS, CONTACT_SIDE_COLUMNS, type ContactColumn,
} from "./customerFormLayout";

/** Mirrors legacy MAX_CONTACTS (CustomerInfo.js) and the zod `.max(15)` cap in
 *  packages/shared/src/schemas.ts. Raising this alone would produce a 400. */
export const MAX_CONTACTS = 15;

/** One editable row. Every text field is a string so the inputs stay controlled; `isPrivate` is the "administrators only" flag
 *  (30/09/2026); `sortOrder` is derived from the array index on save, never edited. */
export type ContactForm = Required<Omit<CustomerContactInput, "sortOrder">>;

const CONTACT_FIELDS = [
  "name",
  "phone1",
  "phone2",
  "email",
  "section",
  "brandName",
  "wechat",
  "team",
] as const;

/** Server-side caps (customerContactSchema) mirrored so the browser stops the
 *  user before the API has to reject the whole 15-row form. */
const MAX_LEN: Record<(typeof CONTACT_FIELDS)[number], number> = {
  name: 120,
  phone1: 60,
  phone2: 60,
  email: 160,
  section: 120,
  brandName: 120,
  wechat: 80,
  team: 120,
};

export function blankContact(): ContactForm {
  return {
    name: "",
    phone1: "",
    phone2: "",
    email: "",
    wechat: "",
    section: "",
    brandName: "",
    team: "",
    isPrivate: false,
  };
}

/** A row the user never filled in. Blank rows are dropped silently on save. */
export const isBlank = (c: ContactForm) => CONTACT_FIELDS.every((k) => c[k].trim() === "");

/**
 * Blank rows are dropped, the rest are numbered by position. A row that carries
 * data but no name would be silently discarded by the API (name is NOT NULL), so
 * it is refused instead — `row` is 1-based, ready for `customers.errorContactName`.
 * Callers discriminate with `Array.isArray(result)`.
 */
export function contactsToInput(
  contacts: ContactForm[],
): CustomerContactInput[] | { error: { row: number } } {
  const nameless = contacts.findIndex((c) => !isBlank(c) && c.name.trim() === "");
  if (nameless >= 0) return { error: { row: nameless + 1 } };
  if (contacts.every(isBlank)) return { error: { row: 1 } };
  return contacts.filter((c) => !isBlank(c)).map((c, i) => ({ ...c, sortOrder: i }));
}

const LABEL: Record<ContactColumn, string> = {
  name: "customers.contactName", phone1: "customers.phone1", email: "common.email", section: "customers.section",
  phone2: "customers.phone2", brandName: "customers.brandName", wechat: "customers.wechat", team: "customers.team",
};
const TYPE: Partial<Record<ContactColumn, "email" | "tel">> = { phone1: "tel", phone2: "tel", email: "email" };
const inputCls =
  "h-8 w-full rounded-xs border border-line bg-surface px-1.5 text-xs text-ink outline-none focus:border-brand disabled:opacity-60";
/** Cột tên dính trái khi bảng cuộn ngang; nút xoá dính phải. */
const pinLeft = "sticky left-0 z-10 bg-surface";
const pinRight = "sticky right-0 z-10 bg-surface";

/**
 * Bảng người liên hệ (19/09/2026, cùng chuẩn bảng dòng hàng — customerFormLayout.ts): mỗi người một hàng, bốn cột chính, cột phụ
 * (điện thoại 2, thương hiệu, WeChat, nhóm) do trang quyết định hiện hay ẩn. Trước đây mỗi người là một thẻ tám ô xếp ba hàng.
 */
export function CustomerContactsEditor({
  contacts, onChange, disabled = false, showSide = true, privacy = false,
}: {
  contacts: ContactForm[];
  onChange: (next: ContactForm[]) => void;
  disabled?: boolean;
  showSide?: boolean;
  /** Hiện nút khoá "Chỉ quản trị viên xem" trong ô tên — chỉ quản trị viên (30/09/2026). */
  privacy?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const set = (i: number, patch: Partial<ContactForm>) =>
    onChange(contacts.map((c, j) => j === i ? { ...c, ...patch } : c));
  const columns: ContactColumn[] = [...CONTACT_MAIN_COLUMNS, ...(showSide ? CONTACT_SIDE_COLUMNS : [])];
  const minRem = CONTACT_EDGE_COLUMNS_REM + columns.reduce((sum, c) => sum + (CONTACT_COLUMN_WIDTH[c] ?? CONTACT_FLEX_MIN_REM), 0);
  return (
    <div className="overflow-x-auto" tabIndex={0}>
      <table className="w-full table-fixed border-collapse text-sm" style={{ minWidth: `${minRem}rem` }}>
        <colgroup>
          <col style={{ width: "2rem" }} />
          {columns.map((c) => {
            const w = CONTACT_COLUMN_WIDTH[c];
            return <col key={c} style={w ? { width: `${w}rem` } : undefined} />;
          })}
          <col style={{ width: "2rem" }} />
        </colgroup>
        <thead>
          <tr className="[&>th]:border-b [&>th]:border-line [&>th]:bg-sunken/60 [&>th]:px-1.5 [&>th]:py-2 [&>th]:text-left [&>th]:align-bottom [&>th]:text-2xs [&>th]:font-semibold [&>th]:uppercase [&>th]:leading-tight [&>th]:tracking-wide [&>th]:text-ink-faint">
            <th className="!text-right">#</th>
            {columns.map((c) => (
              <th key={c} className={c === "name" ? `${pinLeft} bg-sunken` : undefined}>{t(LABEL[c])}</th>
            ))}
            <th className={`${pinRight} bg-sunken`}><span className="sr-only">{t("common.removeContact")}</span></th>
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="px-4 py-6 text-center text-xs text-ink-faint">{t("customers.noContacts")}</td>
            </tr>
          )}
          {contacts.map((c, i) => (
            <tr key={i} className="[&>td]:border-b [&>td]:border-line [&>td]:px-1.5 [&>td]:py-1.5">
              <td className="text-right font-mono tabular text-2xs text-ink-faint">{String(i + 1).padStart(2, "0")}</td>
              {columns.map((key) => (
                <td key={key} className={key === "name" ? pinLeft : undefined}>
                  <div className="flex items-center gap-1">
                  <input
                    id={`${id}-${i}-${key}`}
                    type={TYPE[key]}
                    aria-label={`${t(LABEL[key])} · ${t("customers.contactPersonN", { n: i + 1 })}`}
                    // Tên bắt buộc khi hàng có dữ liệu, hoặc khi đây là người liên hệ duy nhất (khách phải có ít nhất một người).
                    required={key === "name" && (!isBlank(c) || contacts.length === 1)}
                    maxLength={MAX_LEN[key]}
                    disabled={disabled}
                    value={c[key]}
                    title={c[key] || undefined}
                    className={`${inputCls} ${TYPE[key] === "tel" ? "font-mono tabular" : ""}`}
                    onChange={(e) => set(i, { [key]: e.target.value })}
                  />
                  {key === "name" && privacy ? (
                    <button
                      type="button"
                      disabled={disabled}
                      aria-pressed={c.isPrivate}
                      aria-label={t("customers.contactPrivateToggle", { n: i + 1 })}
                      title={t("customers.contactPrivate")}
                      onClick={() => set(i, { isPrivate: !c.isPrivate })}
                      className={`grid size-7 shrink-0 place-items-center rounded-xs border disabled:opacity-40 ${c.isPrivate ? "border-brand bg-brand text-brand-on" : "border-line text-ink-faint hover:text-ink"}`}
                    >
                      {c.isPrivate ? <Lock className="size-3.5" aria-hidden="true" /> : <LockOpen className="size-3.5" aria-hidden="true" />}
                    </button>
                  ) : null}
                  </div>
                </td>
              ))}
              <td className={`text-center ${pinRight}`}>
                <button type="button" disabled={disabled} onClick={() => onChange(contacts.filter((_, j) => j !== i))}
                  aria-label={`${t("common.removeContact")} ${i + 1}`} className="text-ink-faint hover:text-danger disabled:opacity-40">
                  <Trash2 className="size-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
